import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { titleCase } from '../../lib/format'
import type { ListResponse, Organization as Org, RoleCoverage, TeamMember } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, Textarea,
} from '../../components/ui'
import { CheckIcon } from '../../components/icons'

export default function Organization() {
  const { user } = useAuth()
  const orgId = user?.org_id
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const orgQuery = useQuery({
    queryKey: ['leader', 'org', orgId],
    queryFn: () => api.get<{ organization: Org; members: unknown[] }>(`/organizations/${orgId}`),
    enabled: Boolean(orgId),
  })
  const coverageQuery = useQuery({
    queryKey: ['leader', 'org-coverage', orgId],
    queryFn: () => api.get<RoleCoverage>(`/organizations/${orgId}/role-coverage`),
    enabled: Boolean(orgId) && orgQuery.data?.organization.type === 'cooperative',
  })
  const teamQuery = useQuery({
    queryKey: ['leader', 'team'],
    queryFn: () => api.get<ListResponse<TeamMember>>('/leader/team'),
  })

  const saveOrg = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put(`/organizations/${orgId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'org', orgId] })
      setEditing(false)
    },
  })
  const appointReporter = useMutation({
    mutationFn: (reporter_uid: string) => api.post('/leader/organization/appoint-reporter', { reporter_uid }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leader', 'org', orgId] }),
  })
  const addDirector = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/leader/organization/directors', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leader', 'org', orgId] }),
  })
  const assignRole = useMutation({
    mutationFn: ({ uid, role_key }: { uid: string; role_key: string }) =>
      api.post(`/organizations/${orgId}/members/${uid}/roles`, { role_key, action: 'assign' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leader', 'org-coverage', orgId] }),
  })

  if (!orgId) {
    return <Alert tone="warning">You are not linked to an organisation yet.</Alert>
  }
  if (orgQuery.isError) return <Alert tone="danger">{(orgQuery.error as Error).message}</Alert>
  if (orgQuery.isLoading || !orgQuery.data) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
  }

  const org = orgQuery.data.organization
  const team = teamQuery.data?.items ?? []
  const isCooperative = org.type === 'cooperative'

  function onSaveOrg(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    saveOrg.mutate({
      name: String(form.get('name') ?? '').trim(),
      address: String(form.get('address') ?? '').trim(),
      registration_no: String(form.get('registration_no') ?? '').trim(),
      tax_id: String(form.get('tax_id') ?? '').trim(),
    })
  }

  function onAddDirector(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    addDirector.mutate({
      email: String(form.get('email') ?? '').trim(),
      position: String(form.get('position') ?? '').trim(),
      years_experience: String(form.get('years_experience') ?? '').trim(),
    })
    e.currentTarget.reset()
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{org.name}</h1>
          <p className="page-head__sub">{titleCase(org.type)} · {org.registration_no || 'No registration number on file'}</p>
        </div>
        {!editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>}
      </div>

      {editing ? (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Organisation details" />
          <CardBody>
            <form onSubmit={onSaveOrg}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {saveOrg.isError && (
                  <Alert tone="danger">
                    {saveOrg.error instanceof ApiError ? saveOrg.error.message : 'Could not save.'}
                  </Alert>
                )}
                <Input label="Name" name="name" defaultValue={org.name} required />
                <Textarea label="Address" name="address" defaultValue={org.address} rows={2} />
                <div className="grid grid--2">
                  <Input label="Registration number" name="registration_no" defaultValue={org.registration_no} />
                  <Input label="Tax ID" name="tax_id" defaultValue={org.tax_id} />
                </div>
                <div className="row">
                  <Button type="submit" loading={saveOrg.isPending} confirm={{ title: "Save these changes?" }}>Save</Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
          <Card>
            <CardBody>
              <div className="stat__label">Members</div>
              <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>{org.member_count ?? team.length}</div>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <div className="stat__label">Address</div>
              <div style={{ fontWeight: 550 }}>{org.address || '—'}</div>
            </CardBody>
          </Card>
        </div>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Production reporter" sub="Responsible for filing monthly production logs on the org's behalf." />
        <CardBody>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{org.production_reporter_name || 'Not assigned'}</div>
            </div>
            <Select
              value=""
              onChange={(e) => e.target.value && appointReporter.mutate(e.target.value)}
              disabled={appointReporter.isPending}
            >
              <option value="">Reassign to…</option>
              {team.map((m) => <option key={m.uid} value={m.uid}>{m.name || m.email}</option>)}
            </Select>
          </div>
        </CardBody>
      </Card>

      {org.type === 'company' && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Directors" />
          <CardBody>
            <div className="stack" style={{ marginBottom: 'var(--sp-4)' }}>
              {(org.directors ?? []).length === 0 ? (
                <p className="page-head__sub" style={{ margin: 0 }}>No directors listed yet.</p>
              ) : (
                (org.directors ?? []).map((d, i) => (
                  <div key={i} className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{d.name} {d.position && <Badge tone="neutral">{d.position}</Badge>}</span>
                    <span className="page-head__sub" style={{ margin: 0 }}>{d.email}</span>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={onAddDirector}>
              <div className="stack" style={{ gap: 'var(--sp-3)' }}>
                {addDirector.isError && (
                  <Alert tone="danger">
                    {addDirector.error instanceof ApiError ? addDirector.error.message : 'Could not add director.'}
                  </Alert>
                )}
                <div className="grid grid--2">
                  <Input label="Director's email" name="email" type="email" required
                         hint="Must already have an account on the platform." />
                  <Input label="Position" name="position" placeholder="e.g. CEO" />
                </div>
                <Button type="submit" size="sm" loading={addDirector.isPending}>Add director</Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {isCooperative && (
        <Card>
          <CardHeader
            title="Governance role coverage"
            sub={coverageQuery.data
              ? `${coverageQuery.data.mandatory_filled ?? 0} of ${coverageQuery.data.mandatory_total ?? 0} mandatory seats filled`
              : undefined}
          />
          <CardBody flush>
            {coverageQuery.isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Role</th><th>Requirement</th><th>Filled</th><th /></tr></thead>
                  <tbody>
                    {(coverageQuery.data?.rows ?? []).map((r) => (
                      <tr key={r.key}>
                        <td>{r.title}</td>
                        <td><Badge tone={r.requirement === 'mandatory' ? 'warning' : 'neutral'}>{r.requirement}</Badge></td>
                        <td>
                          {r.filled ? (
                            <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckIcon size={14} /> {r.assignee_count}/{r.min_count}
                            </span>
                          ) : `${r.assignee_count ?? 0}/${r.min_count}`}
                        </td>
                        <td className="table__num">
                          <Select
                            value=""
                            onChange={(e) => e.target.value && assignRole.mutate({ uid: e.target.value, role_key: r.key })}
                            disabled={assignRole.isPending}
                          >
                            <option value="">Assign…</option>
                            {team.map((m) => <option key={m.uid} value={m.uid}>{m.name || m.email}</option>)}
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}
